import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";

import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

interface TouchpointLockingContextType {
  isLockingModalOpen: boolean;
  setIsLockingModalOpen: (isOpen: boolean) => void;
  selectedVersion: TouchpointVersionAttributes | null;
  setSelectedVersion: (version: TouchpointVersionAttributes | null) => void;
  onTakeOverEditing: (() => void) | null;
  setOnTakeOverEditing: (callback: (() => void) | null) => void;
}

const TouchpointLockingContext = createContext<TouchpointLockingContextType | undefined>(undefined);

interface TouchpointLockingProviderProps {
  children: ReactNode;
}

export const TouchpointLockingProvider = ({ children }: TouchpointLockingProviderProps) => {
  const [isLockingModalOpen, setIsLockingModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<TouchpointVersionAttributes | null>(null);
  const [onTakeOverEditing, setOnTakeOverEditing] = useState<(() => void) | null>(null);

  const value = useMemo(
    () => ({
      isLockingModalOpen,
      setIsLockingModalOpen,
      selectedVersion,
      setSelectedVersion,
      onTakeOverEditing,
      setOnTakeOverEditing,
    }),
    [isLockingModalOpen, selectedVersion, onTakeOverEditing],
  );

  return (
    <TouchpointLockingContext.Provider value={value}>{children}</TouchpointLockingContext.Provider>
  );
};

export const useTouchpointLocking = () => {
  const context = useContext(TouchpointLockingContext);
  if (context === undefined) {
    throw new Error("useTouchpointLocking must be used within a TouchpointLockingProvider");
  }
  return context;
};
